import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { ProductVariant } from "@/types/product-type"
import { createStockReceipt } from "@/api/stock-movement-api"
import { toast } from "@/hooks/use-toast"
import { useProductWithRelation } from "@/services/product-service"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"

// Form schema for stock entry
const stockEntrySchema = z.object({
  quantity: z.number().min(1, "Quantity must be at least 1"),
  unitCostCents: z.number().min(0, "Unit cost must be positive"),
  currencyCode: z.string().min(1, "Currency code is required").default("USD")
})

type StockEntryFormValues = z.infer<typeof stockEntrySchema>

interface StockEntryModalProps {
  productId: string
  isOpen: boolean
  selectedVariantId: string | null
  productVariants: ProductVariant[]
  onSubmit: () => void
  onCancel: () => void
}

export function StockEntryModal({
  productId,
  isOpen,
  selectedVariantId,
  productVariants,
  onSubmit,
  onCancel
}: StockEntryModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { refresh } = useProductWithRelation(productId);

  const selectedVariant = selectedVariantId
    ? productVariants.find(v => v.id.toString() === selectedVariantId)
    : null

  const form = useForm<StockEntryFormValues>({
    resolver: zodResolver(stockEntrySchema),
    defaultValues: {
      quantity: 1,
      unitCostCents: selectedVariant?.cost || 0,
      currencyCode: "USD",
    },
  })

  const handleSubmit = async (data: StockEntryFormValues) => {
    if (!selectedVariantId) {
      toast({
        title: "Error",
        description: "No variant selected",
        variant: "destructive",
      })
      return
    }

    setIsSubmitting(true)
    try {
      const stockRequest = {
        variantId: selectedVariantId,
        quantity: data.quantity,
      }

      await createStockReceipt(stockRequest)

      toast({
        title: "Success",
        description: `Added ${data.quantity} units to stock for ${selectedVariant?.name}`,
      })

      form.reset()
      onSubmit()
      refresh()
    } catch (error) {
      console.error("Error adding stock:", error)
      toast({
        title: "Error",
        description: "Failed to add stock entry. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
      refresh()
    }
  }

  const handleCancel = () => {
    form.reset()
    onCancel()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleCancel() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Stock Entry</DialogTitle>
          <DialogDescription>
            {selectedVariant
              ? `Adding stock for variant: ${selectedVariant.name} (${selectedVariant.sku})`
              : "Select a variant to add stock"
            }
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="quantity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Quantity</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="1"
                      placeholder="1"
                      value={field.value === 0 || field.value === undefined ? "" : field.value}
                      onChange={(e) => {
                        const raw = e.target.value
                        if (raw === "") {
                          field.onChange(undefined as unknown as number)
                          return
                        }
                        const parsed = parseInt(raw, 10)
                        if (!Number.isNaN(parsed)) {
                          field.onChange(parsed)
                        }
                      }}
                      onBlur={(e) => {
                        if (e.target.value === "") {
                          field.onChange(1)
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || !selectedVariantId}>
                {isSubmitting ? "Adding..." : "Add Stock"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
